import React, { useState, useEffect, useRef } from 'react';
import { Send, X, User, Clock, CheckCheck, MessageSquare, Paperclip, Smile, Image, FileText, HelpCircle, Zap, ArrowRight } from 'lucide-react';
import { useUserStore } from '../../../stores/userStore';
import { TRANSITIONS } from '../../../utils/animations';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';

interface Message {
  id: string;
  sender: 'user' | 'agent' | 'system';
  text: string;
  timestamp: Date;
  status: 'sent' | 'delivered' | 'read';
  attachment?: {
    type: 'image' | 'file';
    url: string;
    name: string;
  };
  isAI?: boolean;
}

interface LiveChatSupportProps {
  onClose: () => void;
}

// AI responses for common questions
const AI_RESPONSES: Record<string, string> = {
  // Original responses
  'plano personalizado': `Para criar um plano personalizado, siga estes passos:

1. Vá para a seção "Ideias de Conteúdo" no dashboard
2. Clique em "Criar Meu Plano Personalizado"
3. Responda às perguntas sobre seu objetivo, tipo de conteúdo, etc.
4. Clique em "Gerar Plano"

Você pode então salvar o plano, baixá-lo ou adicioná-lo ao seu calendário.`,

  'conectar redes sociais': `Para conectar suas redes sociais:

1. Vá para "Configurações" no menu lateral
2. Clique na aba "Redes Sociais"
3. Clique no botão "Conectar" ao lado da plataforma desejada
4. Siga as instruções para autorizar a Kontty

Isso permitirá que geremos ideias personalizadas para cada plataforma.`,

  'calendário': `Para usar o calendário de conteúdo:

1. Acesse "Calendário" no menu lateral
2. Clique em qualquer data para adicionar um evento
3. Preencha os detalhes do conteúdo
4. Você pode vincular ideias salvas ou planos de conteúdo
5. Defina recorrência e lembretes se necessário

O calendário ajuda a organizar sua estratégia de publicação.`,

  'análises': `Para acessar as análises:

1. Vá para "Análises" no menu lateral
2. Clique em "Adicionar Dados" para inserir métricas de desempenho
3. Você pode fazer upload de screenshots para análise automática
4. Visualize gráficos e insights sobre seu desempenho
5. Compare métricas ao longo do tempo

As análises ajudam a entender o que está funcionando melhor.`,

  'preços': `Temos três planos disponíveis:

1. **Grátis**: 5 ideias/mês, 1 plano/mês, calendário básico
2. **Premium** (R$49/mês ou R$499/ano): 30 ideias/mês, 2 planos/mês, análises básicas
3. **Pro** (R$99/mês ou R$999/ano): 100 ideias/mês, 5 planos/mês, análises avançadas, suporte prioritário

Você pode alterar seu plano a qualquer momento na seção "Preços".`,

  'nicho': `Para descobrir seu nicho ideal:

1. Vá para "Ideias de Conteúdo"
2. Clique em "Descubra seu Nicho"
3. Informe sobre o que você quer postar
4. Nossa IA gerará opções de nichos específicos
5. Selecione o que mais combina com você
6. Personalize os detalhes
7. Gere um plano completo para esse nicho

Isso ajudará a focar sua estratégia de conteúdo.`,

  // New responses for website functionality
  'mudar senha': `Para alterar sua senha:

1. Acesse "Configurações" no menu lateral
2. Clique na aba "Segurança"
3. Preencha sua senha atual e a nova senha
4. Clique em "Atualizar Senha"

Por motivos de segurança, recomendamos usar uma senha forte com letras, números e caracteres especiais.`,

  'alterar nome': `Para alterar seu nome de usuário:

1. Acesse "Configurações" no menu lateral
2. Clique na aba "Perfil"
3. Edite seu nome no campo "Nome Completo"
4. Clique em "Salvar Alterações"

Seu nome atualizado aparecerá em todo o dashboard.`,

  'alterar email': `Para alterar seu email:

1. Acesse "Configurações" no menu lateral
2. Clique na aba "Perfil"
3. Edite seu email no campo correspondente
4. Clique em "Salvar Alterações"

Você receberá um email de confirmação no novo endereço.`,

  'cancelar assinatura': `Para cancelar sua assinatura:

1. Acesse "Preços" no menu lateral
2. Clique em "Gerenciar Assinatura"
3. Selecione "Cancelar Assinatura"
4. Confirme sua decisão

Você continuará tendo acesso aos recursos premium até o final do período pago.`,

  'salvar ideias': `Para salvar ideias de conteúdo:

1. Vá para "Ideias de Conteúdo" no menu lateral
2. Gere novas ideias clicando em "Gerar Novas Ideias"
3. Quando encontrar uma ideia que goste, clique no ícone de marcador (bookmark)
4. A ideia será salva e você poderá acessá-la em "Meus Planos"

Você pode salvar quantas ideias quiser para usar mais tarde.`,

  'exportar plano': `Para exportar um plano de conteúdo:

1. Vá para "Meus Planos" no menu lateral
2. Encontre o plano que deseja exportar
3. Clique no ícone de download
4. Escolha o formato desejado (HTML)

O plano será baixado para seu computador e você poderá abri-lo em qualquer navegador.`,

  'adicionar ao calendário': `Para adicionar um plano ao calendário:

1. Vá para "Meus Planos" no menu lateral
2. Encontre o plano que deseja adicionar
3. Clique no ícone de calendário
4. Confirme as datas e horários na janela que aparece
5. Clique em "Adicionar ao Calendário"

Os eventos serão adicionados ao seu calendário Kontty.`,

  'notificações': `Para gerenciar suas notificações:

1. Acesse "Configurações" no menu lateral
2. Clique na aba "Notificações"
3. Ative ou desative os tipos de notificações que deseja receber
4. Clique em "Salvar Preferências"

Você pode personalizar notificações para novas ideias, atualizações da plataforma e mais.`,

  'suporte': `Para obter suporte:

1. Acesse "Ajuda" no menu lateral
2. Escolha entre as opções disponíveis:
   - Documentação: guias e tutoriais
   - Suporte: formulário de contato
   - Chat ao vivo (para usuários Pro)

Nossa equipe está disponível para ajudar com qualquer dúvida.`,

  'gerar ideias': `Para gerar novas ideias de conteúdo:

1. Vá para "Ideias de Conteúdo" no menu lateral
2. Clique em "Gerar Novas Ideias"
3. Aguarde enquanto nossa IA cria sugestões personalizadas
4. Salve as ideias que gostar clicando no ícone de marcador

Você pode gerar ideias para diferentes plataformas conectando suas redes sociais.`,

  'conectar instagram': `Para conectar sua conta do Instagram:

1. Vá para "Configurações" no menu lateral
2. Clique na aba "Redes Sociais"
3. Encontre o Instagram na lista e clique em "Conectar"
4. Faça login na sua conta do Instagram quando solicitado
5. Autorize a Kontty a acessar sua conta

Após conectar, poderemos gerar ideias específicas para Instagram.`,

  'conectar youtube': `Para conectar sua conta do YouTube:

1. Vá para "Configurações" no menu lateral
2. Clique na aba "Redes Sociais"
3. Encontre o YouTube na lista e clique em "Conectar"
4. Faça login na sua conta do Google quando solicitado
5. Autorize a Kontty a acessar sua conta do YouTube

Após conectar, poderemos gerar ideias específicas para YouTube.`,

  'conectar tiktok': `Para conectar sua conta do TikTok:

1. Vá para "Configurações" no menu lateral
2. Clique na aba "Redes Sociais"
3. Encontre o TikTok na lista e clique em "Conectar"
4. Faça login na sua conta do TikTok quando solicitado
5. Autorize a Kontty a acessar sua conta

Após conectar, poderemos gerar ideias específicas para TikTok.`,

  'adicionar dados': `Para adicionar dados de desempenho:

1. Vá para "Análises" no menu lateral
2. Clique em "Adicionar Dados"
3. Selecione a plataforma (Instagram, YouTube, TikTok)
4. Preencha os dados de visualizações, curtidas e comentários
5. Opcionalmente, faça upload de um screenshot para análise automática
6. Clique em "Adicionar Dados"

Quanto mais dados você adicionar, melhores serão nossas análises e recomendações.`,

  'criar evento': `Para criar um evento no calendário:

1. Vá para "Calendário" no menu lateral
2. Clique em uma data no calendário
3. Preencha os detalhes do evento:
   - Título
   - Horário de início e fim
   - Plataforma
   - Prioridade e status (recursos premium)
4. Clique em "Criar"

Você pode visualizar seus eventos em formato de mês, semana ou dia.`,

  'fazer upgrade': `Para fazer upgrade do seu plano:

1. Vá para "Preços" no menu lateral
2. Compare os diferentes planos disponíveis
3. Escolha entre pagamento mensal ou anual (economize 20% no anual)
4. Clique em "Começar Agora" no plano desejado
5. Complete o processo de pagamento

Seu acesso aos recursos premium será imediato após a confirmação do pagamento.`,

  'default': `Posso ajudar com várias funcionalidades da Kontty:

- Criação de planos personalizados
- Geração de ideias de conteúdo
- Uso do calendário
- Análise de métricas
- Configuração de conta
- Descoberta de nicho
- Alteração de senha ou dados pessoais
- Gerenciamento de assinatura
- Exportação de planos
- Conexão de redes sociais
- Suporte técnico

Por favor, me diga mais sobre o que você precisa.`
};

export default function LiveChatSupport({ onClose }: LiveChatSupportProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentStatus, setAgentStatus] = useState<'connecting' | 'online' | 'offline'>('connecting');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const isPro = useUserStore(state => state.isPro);
  const isAdmin = useUserStore(state => state.isAdmin);
  const currentUser = useUserStore(state => state.currentUser);

  // Common emojis for quick selection
  const commonEmojis = ['👋', '👍', '👏', '🙏', '😊', '🎉', '❤️', '🔥', '✅', '⭐'];

  // Simulate agent connecting
  useEffect(() => {
    const timer = setTimeout(() => {
      setAgentStatus('online');
      setAgentName('Ana Silva');
      
      // Add welcome message
      setMessages([
        {
          id: crypto.randomUUID(),
          sender: 'agent',
          text: 'Olá! Sou Ana da equipe de suporte da Kontty. Como posso ajudar você hoje?',
          timestamp: new Date(),
          status: 'read',
          isAI: true
        },
        {
          id: crypto.randomUUID(),
          sender: 'system',
          text: 'Dica: Você pode perguntar sobre qualquer funcionalidade da plataforma, como alterar senha, conectar redes sociais, exportar planos, gerenciar assinatura, ou qualquer outra dúvida que tenha.',
          timestamp: new Date(),
          status: 'read'
        }
      ]);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Generate AI response based on user message
  const generateAIResponse = (userMessage: string): string => {
    const lowerCaseMessage = userMessage.toLowerCase();
    
    // Check for keyword matches - expanded to include more topics
    if (lowerCaseMessage.includes('plano') || lowerCaseMessage.includes('personalizado')) {
      return AI_RESPONSES['plano personalizado'];
    } else if (lowerCaseMessage.includes('redes sociais') || lowerCaseMessage.includes('conectar')) {
      return AI_RESPONSES['conectar redes sociais'];
    } else if (lowerCaseMessage.includes('instagram')) {
      return AI_RESPONSES['conectar instagram'];
    } else if (lowerCaseMessage.includes('youtube')) {
      return AI_RESPONSES['conectar youtube'];
    } else if (lowerCaseMessage.includes('tiktok')) {
      return AI_RESPONSES['conectar tiktok'];
    } else if (lowerCaseMessage.includes('calendário') || lowerCaseMessage.includes('agenda')) {
      return AI_RESPONSES['calendário'];
    } else if (lowerCaseMessage.includes('evento') || lowerCaseMessage.includes('agendar')) {
      return AI_RESPONSES['criar evento'];
    } else if (lowerCaseMessage.includes('análise') || lowerCaseMessage.includes('métricas') || lowerCaseMessage.includes('desempenho')) {
      return AI_RESPONSES['análises'];
    } else if (lowerCaseMessage.includes('dados') || lowerCaseMessage.includes('estatísticas')) {
      return AI_RESPONSES['adicionar dados'];
    } else if (lowerCaseMessage.includes('preço') || lowerCaseMessage.includes('plano') || lowerCaseMessage.includes('pagamento') || lowerCaseMessage.includes('assinatura')) {
      return AI_RESPONSES['preços'];
    } else if (lowerCaseMessage.includes('upgrade') || lowerCaseMessage.includes('premium') || lowerCaseMessage.includes('pro')) {
      return AI_RESPONSES['fazer upgrade'];
    } else if (lowerCaseMessage.includes('nicho') || lowerCaseMessage.includes('descobrir') || lowerCaseMessage.includes('especializar')) {
      return AI_RESPONSES['nicho'];
    } else if (lowerCaseMessage.includes('senha') || lowerCaseMessage.includes('trocar senha') || lowerCaseMessage.includes('mudar senha')) {
      return AI_RESPONSES['mudar senha'];
    } else if (lowerCaseMessage.includes('nome') || lowerCaseMessage.includes('alterar nome') || lowerCaseMessage.includes('mudar nome')) {
      return AI_RESPONSES['alterar nome'];
    } else if (lowerCaseMessage.includes('email') || lowerCaseMessage.includes('alterar email') || lowerCaseMessage.includes('mudar email')) {
      return AI_RESPONSES['alterar email'];
    } else if (lowerCaseMessage.includes('cancelar') || lowerCaseMessage.includes('cancelamento')) {
      return AI_RESPONSES['cancelar assinatura'];
    } else if (lowerCaseMessage.includes('salvar') && lowerCaseMessage.includes('ideia')) {
      return AI_RESPONSES['salvar ideias'];
    } else if (lowerCaseMessage.includes('exportar') || lowerCaseMessage.includes('baixar')) {
      return AI_RESPONSES['exportar plano'];
    } else if (lowerCaseMessage.includes('adicionar') && lowerCaseMessage.includes('calendário')) {
      return AI_RESPONSES['adicionar ao calendário'];
    } else if (lowerCaseMessage.includes('notificação') || lowerCaseMessage.includes('alerta')) {
      return AI_RESPONSES['notificações'];
    } else if (lowerCaseMessage.includes('suporte') || lowerCaseMessage.includes('ajuda') || lowerCaseMessage.includes('contato')) {
      return AI_RESPONSES['suporte'];
    } else if (lowerCaseMessage.includes('gerar') && lowerCaseMessage.includes('ideia')) {
      return AI_RESPONSES['gerar ideias'];
    } else if (lowerCaseMessage.includes('obrigado') || lowerCaseMessage.includes('valeu')) {
      return 'Por nada! Estou aqui para ajudar com qualquer dúvida sobre a plataforma Kontty. Tem mais alguma pergunta?';
    } else {
      return AI_RESPONSES['default'];
    }
  };

  // Simulate agent typing when user sends a message
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.sender === 'user') {
      setIsTyping(true);
      
      const timer = setTimeout(() => {
        setIsTyping(false);
        
        // Generate AI response
        const response = generateAIResponse(lastMessage.text);
        
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          sender: 'agent',
          text: response,
          timestamp: new Date(),
          status: 'read',
          isAI: true
        }]);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (!message.trim() && !uploadedFile) return;
    
    const newMessage: Message = {
      id: crypto.randomUUID(),
      sender: 'user',
      text: message,
      timestamp: new Date(),
      status: 'sent'
    };
    
    // Add attachment if there's an uploaded file
    if (uploadedFile) {
      const isImage = uploadedFile.type.startsWith('image/');
      newMessage.attachment = {
        type: isImage ? 'image' : 'file',
        url: uploadPreview || '',
        name: uploadedFile.name
      };
      
      // Clear the upload state
      setUploadedFile(null);
      setUploadPreview(null);
    }
    
    setMessages(prev => [...prev, newMessage]);
    setMessage('');
    
    // Simulate message being delivered
    setTimeout(() => {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === newMessage.id 
            ? { ...msg, status: 'delivered' } 
            : msg
        )
      );
    }, 1000);
    
    // Simulate message being read
    setTimeout(() => {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === newMessage.id 
            ? { ...msg, status: 'read' } 
            : msg
        )
      );
    }, 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getMessageStatus = (status: Message['status']) => {
    switch (status) {
      case 'sent':
        return <Clock className="h-3 w-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. O tamanho máximo é 10MB.');
      return;
    }
    
    setUploadedFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // For non-image files, just show the file name
      setUploadPreview(null);
    }
    
    toast.success('Arquivo anexado com sucesso!');
  };

  const handleEmojiClick = (emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Format message text with line breaks
  const formatMessageText = (text: string) => {
    // Sanitize the text first
    const sanitizedText = DOMPurify.sanitize(text);
    // Replace newlines with <br> tags
    return sanitizedText.replace(/\n/g, '<br>');
  };

  // Check if user has access to Pro support (either Pro user or Admin)
  const hasProSupport = isPro() || isAdmin();

  if (!hasProSupport) {
    return (
      <div className="fixed bottom-6 right-6 bg-white rounded-lg shadow-xl p-6 max-w-sm z-50 animate-slide-up">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Suporte Pro</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="text-center py-8">
          <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            Suporte por Chat ao Vivo
          </h4>
          <p className="text-gray-600 mb-6">
            O suporte por chat ao vivo está disponível apenas para usuários do plano Pro.
          </p>
          <button
            onClick={() => {
              onClose();
              window.location.href = '/dashboard/precos';
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Fazer Upgrade para Pro
          </button>
        </div>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 bg-indigo-600 text-white rounded-full p-4 shadow-lg cursor-pointer z-50 hover:bg-indigo-700 transition-colors animate-pulse"
        onClick={() => setIsMinimized(false)}
      >
        <MessageSquare className="h-6 w-6" />
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 bg-white rounded-lg shadow-xl w-full max-w-md z-50 flex flex-col animate-slide-up">
      {/* Header */}
      <div className="bg-indigo-600 text-white p-4 rounded-t-lg flex justify-between items-center">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-3">
            {agentStatus === 'connecting' ? (
              <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent" />
            ) : (
              <User className="h-5 w-5" />
            )}
          </div>
          <div>
            <h3 className="font-medium flex items-center">
              {agentStatus === 'connecting' ? 'Conectando...' : agentName}
              {agentStatus === 'online' && <Zap className="h-4 w-4 ml-1 text-yellow-300 animate-pulse" />}
            </h3>
            <div className="flex items-center text-xs">
              <div className={`w-2 h-2 rounded-full mr-1 ${
                agentStatus === 'online' ? 'bg-green-400' : 'bg-gray-400'
              }`} />
              <span>
                {agentStatus === 'connecting' ? 'Conectando ao suporte' : 'Online'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
            title="Minimizar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
            title="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      {/* Chat Messages */}
      <div className="flex-1 p-4 overflow-y-auto max-h-96 bg-gray-50">
        <div className="space-y-4">
          {messages.map((msg) => (
            <div 
              key={msg.id}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : msg.sender === 'system' ? 'justify-center' : 'justify-start'}`}
            >
              {msg.sender === 'system' ? (
                <div className="bg-indigo-50 text-indigo-700 text-xs p-2 rounded-lg max-w-[90%] border border-indigo-100">
                  <div className="flex items-start">
                    <HelpCircle className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }} />
                  </div>
                </div>
              ) : (
                <div className={`max-w-[80%] ${TRANSITIONS.DEFAULT}`}>
                  {msg.sender === 'agent' && (
                    <div className="flex items-center space-x-1 mb-1 ml-1">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center">
                        <User className="h-3 w-3 text-indigo-600" />
                      </div>
                      <span className="text-xs text-gray-500 flex items-center">
                        {agentName}
                        {msg.isAI && <Zap className="h-3 w-3 ml-1 text-yellow-500" title="AI-powered response" />}
                      </span>
                    </div>
                  )}
                  <div className={`rounded-lg p-3 ${
                    msg.sender === 'user' 
                      ? 'bg-indigo-600 text-white rounded-tr-none' 
                      : 'bg-white border border-gray-200 rounded-tl-none'
                  }`}>
                    {/* Message text */}
                    <div 
                      className="text-sm whitespace-pre-wrap break-words"
                      dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }}
                    />
                    
                    {/* Attachment - Image */}
                    {msg.attachment?.type === 'image' && (
                      <div className="mt-2">
                        <img 
                          src={msg.attachment.url} 
                          alt="Attached image" 
                          className="max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(msg.attachment?.url, '_blank')}
                        />
                      </div>
                    )}
                    
                    {/* Attachment - File */}
                    {msg.attachment?.type === 'file' && (
                      <div className="mt-2 flex items-center p-2 bg-gray-100 rounded-lg">
                        <FileText className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-xs text-gray-700 truncate">{msg.attachment.name}</span>
                      </div>
                    )}
                  </div>
                  <div className={`flex items-center mt-1 text-xs text-gray-500 ${
                    msg.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}>
                    <span>{formatTime(msg.timestamp)}</span>
                    {msg.sender === 'user' && (
                      <span className="ml-1">{getMessageStatus(msg.status)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-3 max-w-[80%]">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          
          {/* File upload preview */}
          {uploadedFile && uploadPreview && uploadedFile.type.startsWith('image/') && (
            <div className="flex justify-end">
              <div className="relative max-w-[80%]">
                <img 
                  src={uploadPreview} 
                  alt="Upload preview" 
                  className="max-w-full h-auto rounded-lg border border-gray-200"
                />
                <button 
                  onClick={() => {
                    setUploadedFile(null);
                    setUploadPreview(null);
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
          
          {uploadedFile && !uploadedFile.type.startsWith('image/') && (
            <div className="flex justify-end">
              <div className="relative max-w-[80%] bg-indigo-50 p-3 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-indigo-600 mr-2" />
                  <span className="text-sm text-indigo-700 truncate">{uploadedFile.name}</span>
                </div>
                <button 
                  onClick={() => {
                    setUploadedFile(null);
                    setUploadPreview(null);
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="p-2 border-t border-gray-200 bg-white">
          <div className="flex flex-wrap gap-2">
            {commonEmojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => handleEmojiClick(emoji)}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Input Area */}
      <div className="p-3 border-t border-gray-200">
        <div className="flex items-center">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
          />
          <input
            type="file"
            ref={imageInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*"
          />
          <button 
            className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            title="Anexar arquivo"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <button 
            className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
            onClick={() => imageInputRef.current?.click()}
            title="Anexar imagem"
          >
            <Image className="h-5 w-5" />
          </button>
          <button 
            className="p-2 text-gray-500 hover:text-indigo-600 transition-colors"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            title="Inserir emoji"
          >
            <Smile className="h-5 w-5" />
          </button>
          <div className="flex-1 mx-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Digite sua mensagem..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent resize-none"
              rows={1}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!message.trim() && !uploadedFile}
            className={`p-2 rounded-full ${
              message.trim() || uploadedFile
                ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            } transition-colors`}
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
      
      {/* Quick Help Section */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 mb-2">Perguntas frequentes:</p>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => setMessage("Como alterar minha senha?")}
            className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs hover:bg-indigo-100 transition-colors flex items-center"
          >
            <span>Alterar senha</span>
            <ArrowRight className="h-3 w-3 ml-1" />
          </button>
          <button 
            onClick={() => setMessage("Como conectar minhas redes sociais?")}
            className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs hover:bg-indigo-100 transition-colors flex items-center"
          >
            <span>Conectar redes</span>
            <ArrowRight className="h-3 w-3 ml-1" />
          </button>
          <button 
            onClick={() => setMessage("Como exportar um plano?")}
            className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs hover:bg-indigo-100 transition-colors flex items-center"
          >
            <span>Exportar plano</span>
            <ArrowRight className="h-3 w-3 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}